import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ArrowLeftOnRectangleIcon,
  ArrowDownTrayIcon,
  ArrowsRightLeftIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDownIcon,
  ClipboardIcon,
  ClockIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
  CubeIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  LinkIcon,
  MapPinIcon,
  MegaphoneIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  PrinterIcon,
  QrCodeIcon,
  ServerStackIcon,
  ShareIcon,
  ShieldCheckIcon,
  SignalIcon,
  UserCircleIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const SIDEBAR_SECTIONS_KEY = 'remote-admin:sidebar-sections';
const MASTER_ONLY = ['master'];

const NAV_SECTIONS = [
  {
    id: 'devices',
    label: 'Dispositivos',
    items: [
      { to: '/machines', label: 'Máquinas', Icon: ComputerDesktopIcon },
      { to: '/offline-machines', label: 'Máquinas offline', Icon: ExclamationTriangleIcon },
      { to: '/machine-locations', label: 'Localizações', Icon: MapPinIcon },
      { to: '/inventory', label: 'Inventário', Icon: ComputerDesktopIcon },
      { to: '/printers', label: 'Impressoras', Icon: PrinterIcon },
    ],
  },
  {
    id: 'remote-actions',
    label: 'Ações remotas',
    items: [
      { to: '/templates', label: 'Comandos', Icon: ClipboardIcon, roles: MASTER_ONLY },
      { to: '/send', label: 'Enviar comando', Icon: PaperAirplaneIcon, roles: MASTER_ONLY },
      { to: '/send-bulk', label: 'Comandos em massa', Icon: MegaphoneIcon, roles: MASTER_ONLY },
      { to: '/scripts-send', label: 'Enviar scripts', Icon: DocumentArrowUpIcon, roles: MASTER_ONLY },
      { to: '/applications', label: 'Instalar aplicativos', Icon: ArrowDownTrayIcon, roles: MASTER_ONLY },
      { to: '/public-links', label: 'Links públicos', Icon: LinkIcon, roles: MASTER_ONLY },
      { to: '/bulk-logs', label: 'Histórico de comandos', Icon: ClockIcon, roles: MASTER_ONLY },
      { to: '/agents', label: 'Agents IA', Icon: CpuChipIcon, roles: MASTER_ONLY },
    ],
  },
  {
    id: 'support',
    label: 'Atendimento',
    items: [
      { to: '/attendance-dashboard', label: 'Dashboard', Icon: ChartBarIcon },
      { to: '/tickets', label: 'Tickets', Icon: ChatBubbleLeftRightIcon },
      { to: '/ticket-agendas', label: 'Agendas', Icon: CalendarDaysIcon },
      { to: '/contacts', label: 'Contatos', Icon: IdentificationIcon },
      { to: '/whatsapp-connections', label: 'Conexões WhatsApp', Icon: QrCodeIcon },
    ],
  },
  {
    id: 'it-management',
    label: 'Gestão de TI',
    items: [
      { to: '/collaborators', label: 'Colaboradores', Icon: UserCircleIcon },
      { to: '/it-products', label: 'Produtos de TI', Icon: CubeIcon },
      { to: '/it-service-orders', label: 'Ordens de serviço', Icon: WrenchScrewdriverIcon },
    ],
  },
  {
    id: 'connectivity',
    label: 'Rede e acesso remoto',
    items: [
      { to: '/rustdesk', label: 'Servidor RustDesk', Icon: ServerStackIcon },
      { to: '/rustdesk-peers', label: 'Clientes RustDesk', Icon: ComputerDesktopIcon },
      { to: '/vpn', label: 'Perfis VPN', Icon: ShieldCheckIcon },
      { to: '/vpn-connections', label: 'Conexões VPN', Icon: SignalIcon },
      { to: '/vpn-map', label: 'Mapa VPN', Icon: ShareIcon },
      { to: '/vpn-direct', label: 'Links diretos VPN', Icon: ArrowsRightLeftIcon },
    ],
  },
  {
    id: 'administration',
    label: 'Administração',
    items: [
      { to: '/companies', label: 'Empresas', Icon: BuildingOffice2Icon },
      { to: '/departments', label: 'Departamentos', Icon: MapPinIcon },
      { to: '/users', label: 'Usuários', Icon: UsersIcon },
      { to: '/branding', label: 'Identidade visual', Icon: PhotoIcon },
    ],
  },
];

function normalizePath(value) {
  const normalized = String(value || '/').replace(/\/+$/, '');
  return normalized || '/';
}

function readCurrentRole() {
  try {
    const token = localStorage.getItem('token');
    const payload = token?.split('.')?.[1];
    if (!payload) return 'user';
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = '='.repeat((4 - (base64.length % 4)) % 4);
    return String(JSON.parse(atob(base64 + pad))?.role || 'user').toLowerCase();
  } catch {
    return 'user';
  }
}

function readExpandedSections(currentPath) {
  const activeSection = NAV_SECTIONS.find((section) =>
    section.items.some((item) => normalizePath(item.to) === currentPath)
  );
  const defaults = { [activeSection?.id || 'devices']: true };
  try {
    const saved = JSON.parse(localStorage.getItem(SIDEBAR_SECTIONS_KEY) || '{}');
    return saved
      && typeof saved === 'object'
      && !Array.isArray(saved)
      && Object.keys(saved).length
      ? saved
      : defaults;
  } catch {
    return defaults;
  }
}

const Item = ({ to, label, Icon, onNavigate }) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) => `
      flex min-h-10 items-center gap-3 rounded-md px-3 py-2 transition-colors
      ${isActive
        ? 'bg-sky-700 text-white'
        : 'text-gray-300 hover:bg-gray-700 hover:text-white'}
    `}
    onClick={onNavigate}
  >
    <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
    <span className="min-w-0 truncate">{label}</span>
  </NavLink>
);

function NavSection({ section, expanded, active, onToggle, onNavigate }) {
  return (
    <section>
      <button
        type="button"
        className={`
          flex min-h-9 w-full items-center justify-between gap-3 rounded-md px-3 py-2
          text-left text-xs font-semibold uppercase transition-colors
          ${active ? 'text-sky-300' : 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'}
        `}
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`sidebar-section-${section.id}`}
      >
        <span className="truncate">{section.label}</span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      <div
        id={`sidebar-section-${section.id}`}
        className={`grid transition-[grid-template-rows,opacity] duration-200 ${
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="space-y-1 py-1 pl-2">
            {section.items.map((item) => (
              <Item key={item.to} {...item} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Sidebar({
  onLogout,
  isOpen = false,
  onClose = () => {},
  isDesktopHidden = false,
  onHideDesktop = () => {},
}) {
  const location = useLocation();
  const currentPath = normalizePath(location.pathname);
  const currentRole = readCurrentRole();
  const visibleSections = NAV_SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.roles || item.roles.includes(currentRole)
      ),
    }))
    .filter((section) => section.items.length > 0);
  const [expandedSections, setExpandedSections] = useState(
    () => readExpandedSections(currentPath)
  );
  const handleNavigate = () => onClose();

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    const activeSection = NAV_SECTIONS.find((section) =>
      section.items.some((item) => normalizePath(item.to) === currentPath)
    );
    if (!activeSection) return;
    setExpandedSections((current) => (
      current[activeSection.id] ? current : { ...current, [activeSection.id]: true }
    ));
  }, [currentPath]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_SECTIONS_KEY, JSON.stringify(expandedSections));
    } catch {}
  }, [expandedSections]);

  const toggleSection = (sectionId) => {
    setExpandedSections((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  };

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity md:hidden ${
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-gray-700
          bg-gray-800 text-sm transition-transform
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isDesktopHidden ? 'md:-translate-x-full' : 'md:translate-x-0'}
        `}
      >
        <div className="relative flex h-16 shrink-0 items-center border-b border-gray-700 px-4">
          <h1 className="min-w-0 truncate text-xl font-semibold text-white">Remote Admin</h1>

          <button
            type="button"
            onClick={onHideDesktop}
            className="
              absolute right-2 hidden h-9 w-9 items-center justify-center rounded-md
              text-gray-300 hover:bg-gray-700 hover:text-white md:inline-flex
            "
            aria-label="Ocultar menu"
            title="Ocultar menu"
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={onClose}
            className="
              absolute right-2 inline-flex h-10 w-10 items-center justify-center rounded-md
              text-gray-300 hover:bg-gray-700 hover:text-white md:hidden
            "
            aria-label="Fechar menu"
            title="Fechar menu"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <nav className="flex-1 space-y-2 overflow-y-auto p-3" aria-label="Navegação principal">
          {visibleSections.map((section) => {
            const active = section.items.some(
              (item) => normalizePath(item.to) === currentPath
            );
            return (
              <NavSection
                key={section.id}
                section={section}
                expanded={Boolean(expandedSections[section.id])}
                active={active}
                onToggle={() => toggleSection(section.id)}
                onNavigate={handleNavigate}
              />
            );
          })}
        </nav>

        <div className="shrink-0 border-t border-gray-700 p-3">
          <button
            type="button"
            onClick={() => {
              onClose();
              onLogout?.();
            }}
            className="
              flex min-h-10 w-full items-center justify-center gap-2 rounded-md bg-red-600
              px-3 py-2 text-sm text-white transition-colors hover:bg-red-700
            "
          >
            <ArrowLeftOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
            <span>Sair</span>
          </button>
        </div>
      </aside>
    </>
  );
}
